import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Banner } from '#pplx/frontend/aether/components/Banner/index.js';
import { getButtonClasses } from '#pplx/frontend/aether/components/Button/classes.js';
import { Link } from '#pplx/frontend/aether/components/Link/index.js';
import { ButtonPrimitive } from '#pplx/frontend/aether/primitives/ButtonPrimitive/index.js';
import { TextPrimitive } from '#pplx/frontend/aether/primitives/TextPrimitive/index.js';
import { usePaywallUpsell } from '#pplx/frontend/apps/ask/hooks/pricing-hooks/usePaywallUpsell.js';
import { useApiSource } from '#pplx/frontend/apps/ask/sources/hooks/useApiSource.js';
import { useSourceLimits } from '#pplx/frontend/apps/ask/sources/hooks/useSourceLimits.js';
import { getPremiumDataSourceTypeDescription } from '#pplx/frontend/apps/ask/sources/utilities/getPremiumDataSourceTypeDescription.js';
import { getPremiumDataSourceTypeLabel } from '#pplx/frontend/apps/ask/sources/utilities/getPremiumDataSourceTypeLabel.js';
import { useIsEnterpriseUser } from '#pplx/frontend/auth/useIsEnterpriseUser.js';
import { useIsSubscribed } from '#pplx/frontend/auth/useIsSubscribed.js';
import { ExpandedPremiumSourceBadge } from '#pplx/frontend/components/badges/PremiumSourceBadge.js';
import { PREMIUM_SOURCE_BADGE_LABEL_MESSAGE, PREMIUM_SOURCE_BADGE_SHORT_LABEL_MESSAGE, } from '#pplx/frontend/components/badges/PremiumSourceBadgeMessages.js';
import { ModalOrigin } from '#pplx/frontend/components/types/types.js';
import { $icon } from '#pplx/frontend/icons/icon-sprite.js';
import classNames from 'classnames';
import React, { useCallback, useMemo } from 'react';
import { FormattedMessage, defineMessage, useIntl } from 'react-intl';
const PREMIUM_SOURCE_HELP_URLS = {
    wiley_mcp_cashmere: 'https://www.perplexity.ai/help-center/en/articles/12868503-using-perplexity-with-wiley',
    pitchbook_mcp_cashmere: 'https://www.perplexity.ai/help-center/en/articles/12869442-leveraging-pitchbook-data-with-perplexity',
    statista_mcp_cashmere: 'https://www.perplexity.ai/help-center/en/articles/12869733-using-premium-statista-data-with-perplexity',
    cbinsights_mcp_cashmere: 'https://www.perplexity.ai/help-center/en/articles/12869855-integrating-cb-insights-data-with-perplexity',
    midpage: 'https://www.perplexity.ai/help-center/en/articles/14500415-using-midpage-for-u-s-legal-research-on-perplexity',
    visualdx: 'https://www.perplexity.ai/help-center/en/articles', // PLACEHOLDER URL - to be updated with actual help center article for VisualDx when available
};
/**
 * Tooltip content for premium sources with learn more action
 */
export const PremiumSourceTooltipContent = ({ source, }) => {
    const intl = useIntl();
    // Resolves from the already-cached source lists without mounting another
    // /rest/sources list query variant for tooltip metadata.
    const { source: apiSource } = useApiSource({
        sourceId: source,
        reason: 'premium-source-tooltip',
    });
    const premiumKey = source;
    const staticLabel = getPremiumDataSourceTypeLabel(premiumKey);
    const formattedName = (staticLabel !== source ? staticLabel : null) || apiSource?.display_name || source;
    const staticDescription = getPremiumDataSourceTypeDescription(premiumKey, intl);
    const description = staticDescription || apiSource?.description || '';
    const { getSourceLimit } = useSourceLimits();
    const helpUrl = PREMIUM_SOURCE_HELP_URLS[premiumKey] || apiSource?.source_details?.support_url || undefined;
    const premiumBadgeLabel = intl.formatMessage(PREMIUM_SOURCE_BADGE_LABEL_MESSAGE);
    const premiumBadgeShortLabel = intl.formatMessage(PREMIUM_SOURCE_BADGE_SHORT_LABEL_MESSAGE);
    const showMonthlyLimitBanner = useMemo(() => {
        const { remaining } = getSourceLimit(source);
        return remaining === 0;
    }, [getSourceLimit, source]);
    return (_jsxs("div", { className: "flex flex-col gap-2 p-2", children: [_jsxs("div", { className: "flex items-center justify-between gap-2", children: [_jsx(TextPrimitive, { variant: "smallBold", color: "white", children: formattedName }), _jsx(ExpandedPremiumSourceBadge, { ariaLabel: premiumBadgeLabel, label: premiumBadgeShortLabel })] }), _jsx(TextPrimitive, { variant: "tinyRegular", color: "white", textWrap: "pretty", children: description }), helpUrl && (_jsx("p", { className: "text-pretty text-xs text-white [&>a]:underline-offset-2 [&>a]:decoration-white/20 [&>a]:text-white/75", children: _jsx(Link, { href: helpUrl, target: "_blank", rel: "noopener", variant: "inline", muted: true, children: _jsx(FormattedMessage, { defaultMessage: "Learn about complimentary paid content from {sourceName}", description: "Link text in the premium source tooltip to learn more about a specific paid content provider", id: "9e2a97d574", values: { sourceName: formattedName } }) }) })), showMonthlyLimitBanner && (_jsx("div", { className: "mt-2", children: _jsx(Banner, { variant: "warning", size: "compact", leadingAccessory: $icon('alert-triangle'), title: intl.formatMessage({
                        defaultMessage: "You've reached your monthly limit for this source.",
                        description: 'Warning banner title in the premium source tooltip when the monthly query limit is reached',
                        id: 'c70e7d5423',
                    }) }) })), _jsx(PremiumSourceTooltipFooter, {})] }));
};
const TierButton = ({ variant, onClick, children }) => {
    return (_jsx(ButtonPrimitive, { onClick: onClick, className: classNames(getButtonClasses({
            variant: 'primary',
            size: 'small',
            disabled: false,
            fullWidth: false,
            rounded: false,
            pill: false,
            inline: false,
        }), {
            '!bg-super-strong hover:!bg-super-strong': variant === 'max',
        }), children: children }));
};
const PremiumSourceTooltipFooter = () => {
    const { $t } = useIntl();
    const openPaywallUpsell = usePaywallUpsell();
    const isEnterpriseUser = useIsEnterpriseUser();
    const { hasAccessToProFeatures, isMax } = useIsSubscribed();
    const upgrade = useCallback(() => {
        openPaywallUpsell({
            origin: ModalOrigin.PREMIUM_SOURCE_TOOLTIP,
        });
    }, [openPaywallUpsell]);
    const messages = getUpgradeMessages({
        isEnterpriseUser,
        hasAccessToProFeatures,
        hasAccessToMaxFeatures: isMax,
    });
    if (!messages) {
        return null;
    }
    const isMaxUpsell = hasAccessToProFeatures && !isMax;
    const buttonVariant = isMaxUpsell ? 'max' : 'pro';
    const textClasses = 'text-pretty text-super-primary';
    return (_jsxs(_Fragment, { children: [_jsx("hr", { className: "border-base bg-soft my-2" }), _jsxs("div", { className: "flex flex-col gap-2", children: [_jsx(TextPrimitive, { variant: "tinyRegular", className: textClasses, children: $t(messages.title) }), _jsx(TierButton, { variant: buttonVariant, onClick: upgrade, children: $t(messages.action) })] })] }));
};
function getUpgradeMessages({ hasAccessToProFeatures, hasAccessToMaxFeatures, }) {
    if (!hasAccessToProFeatures) {
        return {
            title: defineMessage({
                defaultMessage: 'Get more queries with Pro',
                description: 'Upsell text in the premium source tooltip footer encouraging free users to upgrade to Pro',
                id: '18cf1a40ee',
            }),
            action: defineMessage({
                defaultMessage: 'Try Perplexity Pro',
                description: 'CTA button label in the premium source tooltip footer to upgrade to Pro',
                id: '8f69a533ed',
            }),
        };
    }
    if (!hasAccessToMaxFeatures) {
        return {
            title: defineMessage({
                defaultMessage: 'Get more queries with Max',
                description: 'Upsell text in the premium source tooltip footer encouraging Pro users to upgrade to Max',
                id: 'a8fc2f826f',
            }),
            action: defineMessage({
                defaultMessage: 'Try Perplexity Max',
                description: 'CTA button label in the premium source tooltip footer to upgrade to Max',
                id: '1cca49e2b8',
            }),
        };
    }
    return null;
}
//# sourceMappingURL=PremiumSourceTooltipContent.js.map